import { createFileRoute } from "@tanstack/react-router";
import { ReactNode, useEffect, useState } from "react";

import { Calendar, Layers, User } from "lucide-react";

import { DropResult } from "@hello-pangea/dnd";

import { Badge } from "@/components/ui/badge";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";

import { useQueryParams } from "@/hooks/use-query-params";

import { groupDataBy, showPriorityIcon, showPriorityText } from "@/lib/utils";

import { initialKanbanColumns, ViewPps } from "../utils/constants";

import {
  JobResponse,
  Job,
  KanbanBoardType,
  SelectedJobType,
  TechnicianType,
} from "@/hooks/services/pps/types/job-orders.type";

import KanbanLoading from "../components/kanban/loading";
import { useGetData, usePutData } from "@/hooks/services/use-query-hooks";
import {
  JobOrderList,
  UpdateJobOrderStatus,
} from "@/hooks/services/pps/handlers/job-orders-api";

import dayjs from "dayjs";
import { TooltipContent, TooltipTrigger } from "@radix-ui/react-tooltip";
import { Tooltip } from "@/components/ui/tooltip";
import CardFooterContent from "../components/kanban/card-footer";
import {
  parseFiltersFromQuery,
  parseSortsFromQuery,
  type DynamicFilter,
  type DynamicSort,
} from "@/components/search-filter-bar";
import { UserAvatars } from "../components/kanban/user-avatar";
import KanbanBoard from "../components/kanban/board";
import KanbanDrawerContent from "../components/kanban/kanban-drawer-content";

export const Route = createFileRoute("/(planner-screens)/planner/kanban")({
  component: RouteComponent,
});

function RouteComponent() {
  const { params } = useQueryParams();

  // Read filters and sorts from URL search params (managed by parent planner.tsx)
  const dynamicFilters: DynamicFilter[] = params.filters
    ? parseFiltersFromQuery(params.filters as string)
    : [];

  const dynamicSorts: DynamicSort[] = params.sorts
    ? parseSortsFromQuery(params.sorts as string)
    : [];

  const { data, isLoading, refetch, isRefetching } = useGetData<JobResponse>(
    JobOrderList({
      ...params,
      viewId: ViewPps.kanban.id,
      limit: 1,
      pageSize: 100,
      ...(dynamicSorts.length > 0 && {
        encodeQuery: { sorts: params.sorts },
      }),
      ...(dynamicFilters.length > 0 && {
        encodeQuery: { filters: params.filters },
      }),
    }),
  );

  const { mutate } = usePutData<any, any>(UpdateJobOrderStatus());

  const [columns, setColumns] = useState<KanbanBoardType<Job>[]>(
    initialKanbanColumns || [],
  );
  const [selectedCard, setSelectedCard] = useState<SelectedJobType>({
    task: {},
    show: false,
  });

  useEffect(() => {
    // Set local loading state to match fetching status (isLoading)
    // This helps prevent hydration errors

    intializeColumn();
  }, [data?.data]);

  const intializeColumn = () => {
    const groupedData = groupDataBy(data?.data as any, "status.statusName");

    setColumns((prev) =>
      prev?.map((item) => {
        const group = groupedData.find(
          (groupItem) => groupItem.groupKey === item.id,
        );

        return {
          ...item,
          tasks: group?.items || [],
        };
      }),
    );
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const sourceColumn = columns.find((col) => col.id === source.droppableId);
    const destinationColumn = columns.find(
      (col) => col.id === destination.droppableId,
    );

    if (!sourceColumn || !destinationColumn) {
      return;
    }

    const sourceTasks = Array.from(sourceColumn.tasks);
    const destinationTasks = Array.from(destinationColumn.tasks);

    const [movedTask] = sourceTasks.splice(source.index, 1);
    destinationTasks.splice(destination.index, 0, movedTask);

    setColumns((prevColumns) =>
      prevColumns?.map((col) => {
        if (col.id === sourceColumn.id) {
          return { ...col, tasks: sourceTasks };
        }
        if (col.id === destinationColumn.id) {
          return { ...col, tasks: destinationTasks };
        }
        return col;
      }),
    );

    if (sourceColumn.title !== destinationColumn.title) {
      mutate({
        jobId: Number(movedTask.id),
        statusId: destinationColumn.statusId,
      });
    }
  };

  const CardComponent = ({ task }: { task?: Job }): JSX.Element => {
    if (!task) {
      return <div>No task available</div>;
    }

    const technicianCount =
      task.ppsTechnicians.length > 0 ? task.ppsTechnicians.length : 0;

    return (
      <Card
        onClick={() => setSelectedCard((prev) => ({ task, show: !prev.show }))}
        className="aero:mb-2 aero:gap-0 aero:p-0 aero:bg-white aero:dark:bg-background aero:shadow-sm aero:hover:shadow-md aero:transition-shadow aero:duration-200"
      >
        <CardHeader className="aero:p-4 aero:pb-0">
          <div className="aero:flex aero:items-start aero:justify-between">
            <div className="aero:flex aero:items-center aero:space-x-2">
              <Layers className="aero:h-5 aero:w-5 aero:!text-blue-500" />
              <span className="aero:!text-sm aero:!font-medium aero:!text-gray-500">
                {task?.part?.manufacturer || "-"}
              </span>
            </div>

            <div className="aero:flex aero:items-center aero:gap-2 aero:!text-muted-foreground">
              <span className="aero:!text-sm">
                {dayjs(task.dueDate).format("DD MMM YYYY")}
              </span>
              <Calendar className="aero:h-4 aero:w-4" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="aero:p-4">
          <h2 className="aero:!text-lg aero:!font-semibold aero:mb-2">
            {task?.jobOrderNo}
          </h2>
          <p className="aero:!text-sm aero:!text-gray-600 aero:mb-4">
            {task?.workspace?.description}
          </p>

          <div className="aero:flex aero:flex-col aero:gap-2">
            <div className="aero:flex aero:gap-2 aero:items-center">
              <Badge
                variant="outline"
                className="aero:w-fit aero:!text-gray-700 aero:bg-gray-50 aero:dark:bg-background aero:dark:text-gray-400 aero:hover:bg-gray-50"
              >
                Part No: {task?.part?.partNumber}
              </Badge>
              <span className="aero:!text-xs">
                {task?.standardHours?.slice(0, 2) || "00"} <span>hrs</span>
              </span>
            </div>
          </div>
        </CardContent>

        {/* Footer */}
        <CardFooter className="aero:px-4 aero:py-3 aero:bg-gray-50 aero:dark:bg-background aero:flex aero:items-center aero:justify-between">
          <div className="aero:flex aero:items-center aero:justify-center aero:gap-1">
            <ShowTooltip
              text={
                <div>
                  <span className="aero:!text-sm aero:!font-semibold">
                    {technicianCount}{" "}
                  </span>
                  <span className="aero:!text-xs aero:!text-gray-600">
                    technician{technicianCount > 1 ? "s are" : " is"} assigned
                    to this task
                  </span>
                </div>
              }
            >
              <div>
                <CardFooterContent icon={User} value={technicianCount} />
              </div>
            </ShowTooltip>

            <ShowTooltip
              text={task.priority && showPriorityText(task.priority)}
            >
              {task.priority && showPriorityIcon(task.priority)}
            </ShowTooltip>
          </div>

          <UserAvatars users={task.ppsTechnicians as TechnicianType[]} />
        </CardFooter>
      </Card>
    );
  };

  return (
    <div className="aero:flex aero:flex-col aero:gap-4">
      {/* Subtitle */}
      <div className="aero:flex aero:items-center aero:gap-2">
        <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
        <h2 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
          Kanban Board
        </h2>
      </div>

      <div className="aero:overflow-auto">
        {isLoading || isRefetching ? (
          <KanbanLoading />
        ) : (
          <KanbanBoard
            columns={columns}
            onDragEnd={onDragEnd}
            setColumns={setColumns}
          >
            <CardComponent />
          </KanbanBoard>
        )}
      </div>

      <KanbanDrawerContent
        open={selectedCard.show}
        onOpenChange={(open) =>
          setSelectedCard((prev) => ({
            ...prev,
            show: open,
          }))
        }
        data={selectedCard.task}
        refetch={refetch}
      />
    </div>
  );
}

function ShowTooltip({
  children,
  text,
}: {
  children: ReactNode;
  text: string | ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger>{children}</TooltipTrigger>
      <TooltipContent className="aero:bg-white aero:shadow-lg aero:p-2 aero:ml-20">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}
